import { creating, exist, Id, UUID } from '@campaign-portal/namespace/common/id';
import { CreditType, WhatsappBSPType } from '@campaign-portal/namespace/common/enums';
import { Partner, PartnerFinancialSettings } from '@campaign-portal/namespace/entities/partners/specs';
import { PackRecord } from '@campaign-portal/namespace/entities/subscriptions/specs';

export interface PartnerEntityConfig<ID = exist | creating> extends Partial<Partner<ID>> {
	id: ID;
	ccId: Id<exist>;
	currencyId: Id<exist>;
}

const createDefaultFinancialSettings = (currencyId: Id<exist>): PartnerFinancialSettings => ({
	currencyId,
	creditType: CreditType.PREPAID,
	creditLimit: 0,
	sendAlerts: false,
});

export class PartnerEntity<ID = exist | creating> implements Partner<ID> {
	[key: string]: unknown;

	id: ID;
	name: string;
	ccId: Id<exist>;
	isReseller: boolean;
	financialSettings: PartnerFinancialSettings;
	balance: number;
	isActive: boolean;
	plans: Id<exist>[];
	packRecords: PackRecord[];
	managerIds: Id<UUID>[];
	currencyId: Id<exist>;
	whatsappBSPType: WhatsappBSPType | null;
	settings?: Partner['settings'];
	isMo: boolean;
	hlrDipping: boolean;
	userIds?: UUID[];
	lockedBalance?: number;
	additionalFields?: Map<string, string>;

	readonly creationDate?: string | null;
	readonly createdBy?: string | null;
	readonly lastUpdated?: string | null;
	readonly modifiedBy?: string | null;

	constructor(config: PartnerEntityConfig<ID>) {
		this.id = config.id;
		this.ccId = config.ccId;
		this.currencyId = config.currencyId;
		this.name = config.name ?? '';
		this.isReseller = config.isReseller ?? false;
		this.financialSettings = config.financialSettings ?? createDefaultFinancialSettings(config.currencyId);
		this.balance = config.balance ?? 0;
		this.isActive = config.isActive ?? false;
		this.plans = config.plans ?? [];
		this.packRecords = config.packRecords ?? [];
		this.managerIds = config.managerIds ?? [];
		this.whatsappBSPType = config.whatsappBSPType ?? null;
		this.settings = config.settings;
		this.isMo = config.isMo ?? false;
		this.hlrDipping = config.hlrDipping ?? false;
		this.userIds = config.userIds;
		this.lockedBalance = config.lockedBalance;
		this.additionalFields = config.additionalFields;
		this.creationDate = config.creationDate;
		this.createdBy = config.createdBy;
		this.lastUpdated = config.lastUpdated;
		this.modifiedBy = config.modifiedBy;
	}

	hasNegativeBalance(): boolean {
		return this.balance < 0;
	}
}
